import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Button,
  Heading,
  Spinner,
  SimpleGrid,
  Badge,
  Link,
} from '@chakra-ui/react';
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, getSubscriptionStatus } from '../firebase';
import { keyframes } from '@emotion/react';
import { initializePaddle } from '@paddle/paddle-js';
import { PADDLE_ENVIRONMENT, PADDLE_TOKEN } from '../paddleConfig';

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const PRICE_IDS = [
  'pri_01krknn0b3hd83gn86zjj41m0b', // monthly
  'pri_01krknqxbt3q4p8t11h1901nhn', // annual
];

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const STATUS_COLORS = {
  active: 'green',
  trialing: 'blue',
  paused: 'yellow',
  past_due: 'orange',
  canceled: 'red',
};

const STATUS_LABELS = {
  active: 'Active',
  trialing: 'Trial',
  paused: 'Paused',
  past_due: 'Past Due',
  canceled: 'Canceled',
};

const SubscriptionInfo = ({ subscription, onRefresh, refreshing }) => {
  const item = subscription.items?.[0];
  const statusColor = STATUS_COLORS[subscription.status] || 'gray';
  const statusLabel = STATUS_LABELS[subscription.status] || subscription.status;

  return (
    <Box
      w="100%"
      maxW="500px"
      mx="auto"
      border="1px solid"
      borderColor={`${statusColor}.200`}
      borderRadius="lg"
      bg={`${statusColor}.50`}
      p={6}
      position="relative"
    >
      <Box
        as="button"
        onClick={onRefresh}
        position="absolute"
        top={3}
        right={3}
        p={1}
        borderRadius="md"
        color="gray.400"
        _hover={{ color: 'gray.600', bg: 'blackAlpha.100' }}
        transition="all 0.2s"
        cursor="pointer"
        opacity={refreshing ? 0.5 : 1}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={refreshing ? { animation: `${spin} 1s linear infinite` } : undefined}
        >
          <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
        </svg>
      </Box>
      <VStack spacing={4}>
        <Badge colorScheme={statusColor} fontSize="sm" px={3} py={1}>
          {statusLabel} Subscription
        </Badge>
        {item && (
          <VStack spacing={1}>
            <Text fontWeight="bold" color="gray.700" fontSize="lg">
              {item.description}
            </Text>
            <Text color="gray.600" fontSize="md">
              {item.currencyCode} {(Number(item.unitAmount) / 100).toFixed(2)}
              {item.billingInterval && (
                <Text as="span" color="gray.500" fontSize="sm">
                  {' '}/ {item.billingInterval}
                </Text>
              )}
            </Text>
          </VStack>
        )}
        {subscription.nextBilledAt && subscription.status === 'active' && (
          <Text color="gray.500" fontSize="sm">
            Next billing: {new Date(subscription.nextBilledAt).toLocaleDateString()}
          </Text>
        )}
        {subscription.canceledAt && (
          <Text color="red.500" fontSize="sm">
            Canceled on: {new Date(subscription.canceledAt).toLocaleDateString()}
          </Text>
        )}
        {subscription.scheduledChange && (
          <Text color="orange.500" fontSize="sm">
            Scheduled: {subscription.scheduledChange.action} on{' '}
            {new Date(subscription.scheduledChange.effectiveAt).toLocaleDateString()}
          </Text>
        )}
        <Link
          href="https://sandbox-customer-portal.paddle.com/cpl_01krkm2ta2v1w2zznfeynz3ykh"
          isExternal
          color="blue.500"
          fontSize="sm"
          fontWeight="medium"
          _hover={{ color: 'blue.600', textDecoration: 'underline' }}
        >
          Manage your subscription
        </Link>
      </VStack>
    </Box>
  );
};

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [paddle, setPaddle] = useState(null);
  const [prices, setPrices] = useState([]);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading] = useState(false);

  const fetchSubscription = useCallback(async () => {
    setSubLoading(true);
    try {
      const result = await getSubscriptionStatus();
      setSubscription(result.data.subscription);
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    } finally {
      setSubLoading(false);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (user) {
      fetchSubscription();
    } else {
      setSubscription(null);
    }
  }, [user, fetchSubscription]);

  const loadPrices = useCallback(async (paddleInstance) => {
    if (PRICE_IDS.length === 0) return;
    setPricesLoading(true);
    try {
      const result = await paddleInstance.PricePreview({
        items: PRICE_IDS.map(id => ({ priceId: id, quantity: 1 })),
      });
      setPrices(result.data.details.lineItems);
    } catch (err) {
      console.error('Failed to load prices:', err);
    } finally {
      setPricesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    initializePaddle({
      environment: PADDLE_ENVIRONMENT,
      token: PADDLE_TOKEN,
      eventCallback: (event) => {
        if (event.name === 'checkout.completed') {
          // Re-fetch subscription from server after a short delay
          // to allow the Paddle webhook to be processed
          setTimeout(fetchSubscription, 3000);
        }
      },
    }).then((paddleInstance) => {
      if (paddleInstance) {
        setPaddle(paddleInstance);
        loadPrices(paddleInstance);
      }
    });
  }, [user, loadPrices, fetchSubscription]);

  const handleCheckout = (priceId) => {
    if (!paddle || !user) return;
    paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      customer: { email: user.email },
      customData: { firebase_user_uid: user.uid },
      settings: {
        displayMode: 'overlay',
        theme: 'light',
      },
    });
  };

  const handleSignIn = async () => {
    setSigningIn(true);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      console.error('Sign-in error:', err.message);
    } finally {
      setSigningIn(false);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
  };

  if (loading) {
    return (
      <Box minH="60vh" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="xl" color="#52c41a" />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box minH="60vh" display="flex" alignItems="center" justifyContent="center">
        <Container maxW="container.sm">
          <VStack spacing={6} textAlign="center">
            <Heading size="lg" color="gray.700">Sign in to your account</Heading>
            <Text color="gray.500">
              Sign in with your Google account to view your profile.
            </Text>
            <Box
              as="button"
              onClick={!signingIn ? handleSignIn : undefined}
              w="100%"
              bg="#fff"
              border="1px solid #D1D5DB"
              borderRadius="6px"
              px="16px"
              py="10px"
              transition="all 0.2s"
              _hover={{ bg: '#F9FAFB' }}
              opacity={signingIn ? 0.6 : 1}
              cursor={signingIn ? 'not-allowed' : 'pointer'}
            >
              <HStack spacing="12px" justify="center">
                {signingIn ? (
                  <Spinner size="sm" color="#374151" />
                ) : (
                  <GoogleIcon />
                )}
                <Text color="#374151" fontSize="sm" fontWeight="500">
                  {signingIn ? 'Signing in...' : 'Sign in with Google'}
                </Text>
              </HStack>
            </Box>
          </VStack>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="60vh" py={16}>
      <Container maxW="container.md">
        <VStack spacing={10} align="center">
          {/* User info */}
          <VStack spacing={4} align="center">
            <Box
              as="img"
              src={user.photoURL}
              alt={user.displayName}
              referrerPolicy="no-referrer"
              w="96px"
              h="96px"
              borderRadius="full"
              objectFit="cover"
            />
            <VStack spacing={1}>
              <Heading size="md" color="gray.700">{user.displayName}</Heading>
              <Text color="gray.500">{user.email}</Text>
            </VStack>
            <Button
              onClick={handleSignOut}
              variant="outline"
              colorScheme="red"
              size="sm"
            >
              Sign Out
            </Button>
          </VStack>

          {/* Subscription section */}
          <Box w="100%">
            {subLoading && !subscription ? (
              <VStack py={8}>
                <Spinner size="lg" color="#52c41a" />
                <Text color="gray.500" fontSize="sm">Loading subscription...</Text>
              </VStack>
            ) : subscription ? (
              <>
                <SubscriptionInfo
                  subscription={subscription}
                  onRefresh={fetchSubscription}
                  refreshing={subLoading}
                />
              </>
            ) : (
              <>
                <Heading size="md" color="gray.700" textAlign="center" mb={6}>
                  Subscription Plans
                </Heading>
                {pricesLoading ? (
                  <VStack py={8}>
                    <Spinner size="lg" color="#52c41a" />
                    <Text color="gray.500" fontSize="sm">Loading plans...</Text>
                  </VStack>
                ) : prices.length > 0 ? (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} maxW="600px" mx="auto">
                    {prices.map((item) => (
                      <Box
                        key={item.price.id}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="lg"
                        p={6}
                        textAlign="center"
                        transition="all 0.2s"
                        _hover={{ borderColor: '#52c41a', shadow: 'md' }}
                      >
                        <VStack spacing={3}>
                          <Text fontWeight="bold" color="gray.700" fontSize="lg">
                            {item.price.name || item.product.name}
                          </Text>
                          {item.price.description && (
                            <Text color="gray.500" fontSize="sm">
                              {item.price.description}
                            </Text>
                          )}
                          <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                            {item.formattedTotals.total}
                          </Text>
                          {item.price.billingCycle && (
                            <Badge colorScheme="green" fontSize="xs">
                              per {item.price.billingCycle.interval}
                            </Badge>
                          )}
                          <Button
                            onClick={() => handleCheckout(item.price.id)}
                            bg="#52c41a"
                            color="white"
                            _hover={{ bg: '#389e0d' }}
                            size="md"
                            w="100%"
                          >
                            Subscribe
                          </Button>
                        </VStack>
                      </Box>
                    ))}
                  </SimpleGrid>
                ) : (
                  <Text color="gray.400" textAlign="center">
                    No plans available at the moment.
                  </Text>
                )}
              </>
            )}
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default Profile;
